import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { ConfirmationData, ConfirmationDialogComponent } from '../../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component';
import { FarmNoteType } from '../../../models/farm-note';
import { FARM_NOTE_ICONS, lookupFarmNoteIcon } from '../../../models/farm-note-icons';
import { FarmNoteTypeService } from '../../../services/farm-note-type.service';

interface EditableType extends FarmNoteType {
  _editing?: boolean;
  _draft?: FarmNoteType;
}

const DEFAULT_COLOR = '#1976d2';
const DEFAULT_ICON = 'sticky-note';

@Component({
  selector: 'kw-farm-note-types-dialog',
  templateUrl: './farm-note-types-dialog.component.html',
  styleUrls: ['./farm-note-types-dialog.component.scss']
})
export class FarmNoteTypesDialogComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  public types: EditableType[] = [];
  public loading = false;
  public saving = false;
  /** True once anything changed, so the opener knows to refresh its type list. */
  private changed = false;

  public iconChoices = FARM_NOTE_ICONS;
  public icon = lookupFarmNoteIcon;
  public labelFor(name: string): string {
    return this.iconChoices.find(i => i.name === name)?.label || '';
  }

  public newType: FarmNoteType = { name: '', color: DEFAULT_COLOR, icon: DEFAULT_ICON, sortOrder: 0 };

  public faPen = faPen;
  public faPlus = faPlus;
  public faTrash = faTrash;

  constructor(
    private typeService: FarmNoteTypeService,
    private errorService: ErrorService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<FarmNoteTypesDialogComponent>,
  ) { }

  ngOnInit() {
    this.load();
  }

  private load() {
    this.loading = true;
    this.typeService.listAll().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (types) => {
        this.types = (types || []).sort((a, b) =>
          (a.sortOrder || 0) - (b.sortOrder || 0) || (a.name || '').localeCompare(b.name || ''));
        this.newType.sortOrder = this.types.length;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.errorService.handleError(err);
      }
    });
  }

  close() {
    this.dialogRef.close(this.changed);
  }

  add() {
    if (!this.newType.name.trim() || this.saving) return;
    this.saving = true;
    this.typeService.create({ ...this.newType, name: this.newType.name.trim() })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.saving = false;
          this.changed = true;
          this.newType = { name: '', color: DEFAULT_COLOR, icon: DEFAULT_ICON, sortOrder: 0 };
          this.load();
        },
        error: (err) => {
          this.saving = false;
          this.errorService.handleError(err);
        }
      });
  }

  startEdit(t: EditableType) {
    t._draft = { ...t };
    t._editing = true;
  }

  cancelEdit(t: EditableType) {
    t._editing = false;
  }

  saveEdit(t: EditableType) {
    if (!t._draft?.name?.trim() || this.saving) return;
    this.saving = true;
    this.typeService.update({ ...t._draft, name: t._draft.name.trim() })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.saving = false;
          this.changed = true;
          this.load();
        },
        error: (err) => {
          this.saving = false;
          this.errorService.handleError(err);
        }
      });
  }

  reactivate(t: EditableType) {
    this.typeService.update({ ...t, isActive: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.changed = true;
        this.load();
      },
      error: (err) => this.errorService.handleError(err)
    });
  }

  confirmDelete(t: EditableType) {
    const data: ConfirmationData = {
      title: 'Remove Note Type',
      confirmMessage: `Remove "${t.name}"? Existing notes keep this label; it just won't be offered for new notes.`,
      confirmLabel: 'Remove',
      cancelLabel: 'Cancel',
      displayComment: false,
      isWarning: false,
    };
    this.dialog.open(ConfirmationDialogComponent, { width: '420px', data })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
        if (result !== true) return;
        this.typeService.delete(t.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.changed = true;
            this.load();
          },
          error: (err) => this.errorService.handleError(err)
        });
      });
  }
}
